import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type ResursEnvironment = 'integration' | 'production';

export interface ResursLastTest {
  testedAt: string;
  success: boolean;
  /** Stable code, e.g. `RESURS_UNAUTHORIZED`. Never provider text. */
  error?: string;
}

/** What the API tells about the configuration. It never carries a secret or a token. */
export interface ResursConfig {
  enabled: boolean;
  environment: ResursEnvironment;
  storeId: string | null;
  /** Integer cents. */
  minAmountCents: number;
  configVersion: number;
  hasClientId: boolean;
  hasClientSecret: boolean;
  lastTest: ResursLastTest | null;
  providerOrigin: string;
  encryptionAvailable: boolean;
  /** Client id, client secret and store id are all set. */
  complete: boolean;
}

export interface ResursConfigInput {
  enabled?: boolean;
  environment?: ResursEnvironment;
  storeId?: string | null;
  minAmountCents?: number;
  /** Omit to keep the stored value. */
  clientId?: string;
  /** Omit to keep the stored value. */
  clientSecret?: string;
}

export interface ResursTestResponse {
  success: boolean;
  config: ResursConfig;
}

const RESURS_QUERY_KEY = ['integrations', 'resurs'] as const;

export function useResursIntegration(enabled: boolean) {
  return useQuery({
    queryKey: RESURS_QUERY_KEY,
    queryFn: () => api.get<ResursConfig>('/integrations/resurs'),
    enabled,
  });
}

function useResursMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESURS_QUERY_KEY });
    },
  });
}

export function useSaveResursConfig() {
  return useResursMutation((input: ResursConfigInput) => api.put<ResursConfig>('/integrations/resurs', input));
}

export function useTestResursConnection() {
  return useResursMutation(() => api.post<ResursTestResponse>('/integrations/resurs/test', {}));
}

export function useDeleteResursCredentials() {
  return useResursMutation(() => api.delete<ResursConfig>('/integrations/resurs/credentials'));
}
